/**
 * Mega menu heycup — écran 6a, bandeau de bureau.
 *
 * Panneaux et déclencheurs sont retrouvés par ATTRIBUT (`data-hey-panneau`,
 * `data-hey-entree`), jamais par position dans un tableau de refs : une entrée
 * de premier niveau peut être un simple lien, sans panneau, et tous les
 * indices suivants se décaleraient — le survol ouvrirait le mauvais panneau.
 *
 * Contrat d'accessibilité, repris de `assets/header-menu.js` qui fait
 * référence dans le thème :
 *
 *   - `inert` sur les panneaux fermés, et pas seulement `visibility: hidden` :
 *     un descendant peut redéclarer sa visibilité et redevenir atteignable au
 *     clavier sans jamais être visible ;
 *   - `aria-expanded` sur le déclencheur, tenu à jour ;
 *   - Échap ferme et REND le focus au déclencheur ;
 *   - flèches gauche/droite entre les entrées de premier niveau, Home/Fin aux
 *     extrémités, flèche bas pour entrer dans le panneau ;
 *   - le survol ouvre après un court délai et ferme après un délai plus long :
 *     un pointeur qui ne fait que traverser n'ouvre rien, et un pointeur qui
 *     descend en diagonale n'a pas à raser le bord.
 */

class HeyMegaMenu extends HTMLElement {
  #ouvert = -1;
  #minuterieOuverture = 0;
  #minuterieFermeture = 0;

  /* Le bandeau n'existe qu'au-dessus de 990 px ; en dessous, la feuille de
     style le met en `display: none` et c'est le tiroir d'Horizon qui prend le
     relais. Le script n'a donc rien à désactiver — sauf une chose : un panneau
     laissé ouvert au moment où la fenêtre passe sous la limite reste marqué
     ouvert, et réapparaît ouvert quand on réélargit. On le referme au passage.
     `matchMedia` plutôt qu'un écouteur de `resize` : il ne se déclenche qu'au
     franchissement, pas à chaque pixel. */
  #largeurBureau = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.addEventListener('pointerleave', this.#surSortie);
    this.addEventListener('focusout', this.#surPerteFocus);
    this.addEventListener('click', this.#surClic);
    this.addEventListener('keydown', this.#surTouche);
    this.addEventListener('pointerenter', this.#surSurvol, true);
    document.addEventListener('keydown', this.#surEchap);
    this.#largeurBureau.addEventListener('change', this.#surLargeur);
  }

  #surLargeur = (e) => {
    if (!e.matches) this.fermer();
  };

  disconnectedCallback() {
    this.removeEventListener('pointerleave', this.#surSortie);
    this.removeEventListener('focusout', this.#surPerteFocus);
    this.removeEventListener('click', this.#surClic);
    this.removeEventListener('keydown', this.#surTouche);
    this.removeEventListener('pointerenter', this.#surSurvol, true);
    document.removeEventListener('keydown', this.#surEchap);
    this.#largeurBureau.removeEventListener('change', this.#surLargeur);
    clearTimeout(this.#minuterieOuverture);
    clearTimeout(this.#minuterieFermeture);
  }

  /* --------------------------------------------------------------- accès */

  #panneau(i) {
    return this.querySelector(`[data-hey-panneau="${i}"]`);
  }

  #declencheur(i) {
    return this.querySelector(`[data-hey-entree="${i}"]`);
  }

  get #tousPanneaux() {
    return this.querySelectorAll('[data-hey-panneau]');
  }

  get #tousDeclencheurs() {
    return this.querySelectorAll('[data-hey-entree]');
  }

  #delai(nom, defaut) {
    const v = Number(this.dataset[nom]);
    return Number.isFinite(v) ? v : defaut;
  }

  /* ----------------------------------------------------- ouvrir / fermer */

  ouvrir(i) {
    clearTimeout(this.#minuterieFermeture);
    if (this.#ouvert === i) return;

    for (const panneau of this.#tousPanneaux) {
      const actif = Number(panneau.dataset.heyPanneau) === i;
      panneau.toggleAttribute('inert', !actif);
      panneau.classList.toggle('hey-mm__panneau--ouvert', actif);
    }
    for (const d of this.#tousDeclencheurs) {
      if (d.hasAttribute('aria-expanded')) {
        d.setAttribute('aria-expanded', String(Number(d.dataset.heyEntree) === i));
      }
    }

    this.#ouvert = i;
    this.classList.add('hey-mm--ouvert');
  }

  fermer({ rendreFocus = false } = {}) {
    clearTimeout(this.#minuterieOuverture);
    if (this.#ouvert < 0) return;

    const precedent = this.#ouvert;
    for (const panneau of this.#tousPanneaux) {
      panneau.setAttribute('inert', '');
      panneau.classList.remove('hey-mm__panneau--ouvert');
    }
    for (const d of this.#tousDeclencheurs) {
      if (d.hasAttribute('aria-expanded')) d.setAttribute('aria-expanded', 'false');
    }

    this.#ouvert = -1;
    this.classList.remove('hey-mm--ouvert');

    // Échap doit rendre le focus, sinon le lecteur d'écran repart du début
    // du document.
    if (rendreFocus) this.#declencheur(precedent)?.focus();
  }

  /* --------------------------------------------------------------- souris */

  #surSurvol = (evenement) => {
    const cible = evenement.target.closest?.('[data-hey-entree]');
    if (!cible) return;

    // Une entree de premier niveau sans panneau — un simple lien — doit
    // FERMER celui qui est ouvert. Sinon le panneau precedent reste affiche
    // sous une entree qui n'a rien a voir avec lui.
    if (!cible.hasAttribute('aria-expanded')) {
      clearTimeout(this.#minuterieOuverture);
      this.fermer();
      return;
    }

    const i = Number(cible.dataset.heyEntree);
    clearTimeout(this.#minuterieFermeture);
    clearTimeout(this.#minuterieOuverture);

    // Un panneau déjà ouvert : on bascule sans délai, le menu est « armé ».
    if (this.#ouvert >= 0) {
      this.ouvrir(i);
      return;
    }
    this.#minuterieOuverture = setTimeout(() => this.ouvrir(i), this.#delai('delaiOuverture', 100));
  };

  #surSortie = () => {
    clearTimeout(this.#minuterieOuverture);
    clearTimeout(this.#minuterieFermeture);
    this.#minuterieFermeture = setTimeout(() => this.fermer(), this.#delai('delaiFermeture', 200));
  };

  /* -------------------------------------------------------------- clavier */

  #surClic = (evenement) => {
    const cible = evenement.target.closest?.('[data-hey-entree][aria-expanded]');
    if (!cible || !this.contains(cible)) return;

    evenement.preventDefault();
    const i = Number(cible.dataset.heyEntree);
    if (this.#ouvert === i) this.fermer({ rendreFocus: true });
    else this.ouvrir(i);
  };

  #surTouche = (evenement) => {
    const cible = evenement.target.closest?.('[data-hey-entree]');
    if (!cible) return;

    const i = Number(cible.dataset.heyEntree);
    const n = this.#tousDeclencheurs.length;
    let suivant = null;

    switch (evenement.key) {
      case 'ArrowRight':
        suivant = (i + 1) % n;
        break;
      case 'ArrowLeft':
        suivant = (i - 1 + n) % n;
        break;
      case 'Home':
        suivant = 0;
        break;
      case 'End':
        suivant = n - 1;
        break;
      case 'ArrowDown':
        // Descendre ouvre le panneau et y place le focus : c'est le geste
        // attendu d'un menu à panneaux.
        if (!cible.hasAttribute('aria-expanded')) return;
        evenement.preventDefault();
        this.ouvrir(i);
        this.#panneau(i)?.querySelector('a, button')?.focus();
        return;
      default:
        return;
    }

    evenement.preventDefault();
    this.#declencheur(suivant)?.focus();
    // Si un panneau était ouvert, le contenu visible suit le focus plutôt
    // que de rester en arrière.
    if (this.#ouvert >= 0) {
      if (this.#panneau(suivant)) this.ouvrir(suivant);
      else this.fermer();
    }
  };

  #surEchap = (evenement) => {
    if (evenement.key !== 'Escape' || this.#ouvert < 0) return;
    evenement.stopPropagation();
    this.fermer({ rendreFocus: true });
  };

  /** Le focus sorti du menu ferme le panneau : sinon il reste ouvert derrière. */
  #surPerteFocus = (evenement) => {
    if (this.#ouvert < 0) return;
    if (evenement.relatedTarget && this.contains(evenement.relatedTarget)) return;
    this.fermer();
  };
}

if (!customElements.get('hey-mega-menu')) {
  customElements.define('hey-mega-menu', HeyMegaMenu);
}
